import type React from "react";
import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { Loader } from "@pbz/ui-kit";

import {
  AbilityContext,
  buildAbilityFromApiResponse,
  RbacConfigContext,
} from "@/utils/casl";
import { orpc } from "@/utils/orpc-client";

export function AbilityProvider({ children }: { children: React.ReactNode }) {
  const {
    data: userAbilitiesData,
    isLoading,
    error,
  } = useQuery(
    orpc.user.getRbacConfigAndRolePermissions.queryOptions({
      input: {},
      queryKey: ["userAbilities"],
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  );

  // Use the ability and rbacConfig from the API response
  const { ability, rbacConfig } = useMemo(() => {
    if (!userAbilitiesData?.data) {
      return { ability: null, rbacConfig: null };
    }

    const { rbacConfig: apiRbacConfig, result: abilities } =
      userAbilitiesData.data;

    // Recreate the ability using the rules from the API response
    // return buildAbilityFromApiResponse(apiRbacConfig, abilities);
    const recreatedAbility = buildAbilityFromApiResponse(
      apiRbacConfig,
      abilities,
    );

    return {
      ability: recreatedAbility,
      rbacConfig: apiRbacConfig,
    };
  }, [userAbilitiesData]);

  if (isLoading || !ability || !rbacConfig) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="text-center">
          <Loader className="h-16 w-16 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Error loading user abilities - render error component
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="text-center">
          <div className="h-8 w-8 rounded-full bg-red-500 mx-auto mb-4 flex items-center justify-center">
            <span className="text-white text-sm">!</span>
          </div>
          <p className="text-red-500 font-semibold">
            Failed to load permissions
          </p>
          <p className="text-muted-foreground text-sm mt-2">
            Please refresh the page or contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <AbilityContext.Provider value={ability}>
      <RbacConfigContext.Provider value={rbacConfig}>
        {children}
      </RbacConfigContext.Provider>
    </AbilityContext.Provider>
  );
}
